import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import { Flame, Bug, Key, Sparkles, CheckCircle2, RotateCcw, Volume2, Mic } from 'lucide-react';

export const SimulationBar: React.FC = () => {
  const { triggerBug, agentMode, setAgentMode, geminiApiKey, setGeminiApiKey, activeIncident, systemStatus, resetAll } =
    useAutoFix();
  const [isSpeaking, setIsSpeaking] = useState(false);

  const speakVoiceBriefing = () => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    window.speechSynthesis.cancel();
    setIsSpeaking(true);

    let narrative = '';
    if (activeIncident && activeIncident.patch) {
      narrative = `AutoFix WebMCP sentinel report. Runtime exception ${activeIncident.crashDump.errorType} was intercepted. AST code hotpatch was synthesized and verified by W3C WebMCP in under 250 milliseconds. Memory state restored with zero downtime.`;
    } else {
      narrative = `Welcome to AutoFix.run, the autonomous self-healing web agent built for the WebMCP challenge. We monitor live JavaScript exceptions and heal runtime faults in memory via navigator.modelContext with zero page reload.`;
    }

    const utterance = new SpeechSynthesisUtterance(narrative);
    utterance.rate = 1.05;
    utterance.pitch = 1.0;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 14px',
        background: 'rgba(15, 23, 42, 0.95)',
        borderBottom: '1px solid var(--border)',
        backdropFilter: 'blur(10px)',
        gap: 12,
        flexShrink: 0,
        flexWrap: 'wrap',
      }}
    >
      {/* Left: Bug Trigger Quick Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginRight: 4 }}>
          <Flame size={14} style={{ color: 'var(--red)' }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', color: 'var(--text-2)' }}>
            SIMULATE FAULT:
          </span>
        </div>

        <button
          className="btn btn-sm"
          onClick={() => triggerBug('bug-1')}
          title="Zero-divide arithmetic fault: Cart total collapses to NaN"
          style={{
            background: 'rgba(239, 68, 68, 0.12)',
            borderColor: 'rgba(239, 68, 68, 0.4)',
            color: '#fca5a5',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Bug size={12} />
          <span>#1 NaN Collapse</span>
        </button>

        <button
          className="btn btn-sm"
          onClick={() => triggerBug('bug-2')}
          title="Business logic fault: 110% discount stacking causes negative checkout"
          style={{
            background: 'rgba(245, 158, 11, 0.12)',
            borderColor: 'rgba(245, 158, 11, 0.4)',
            color: '#fcd34d',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Bug size={12} />
          <span>#2 Negative Price</span>
        </button>

        <button
          className="btn btn-sm"
          onClick={() => triggerBug('bug-3')}
          title="Fatal React render crash: null orders state collapses component tree"
          style={{
            background: 'rgba(139, 92, 246, 0.12)',
            borderColor: 'rgba(139, 92, 246, 0.4)',
            color: '#c4b5fd',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Bug size={12} />
          <span>#3 TypeError Crash</span>
        </button>

        <button
          className="btn btn-sm"
          onClick={() => triggerBug('calc-div-zero')}
          title="Real-world open source crash: ahfarmer/calculator (2.7k stars) 5 ÷ 0"
          style={{
            background: 'rgba(236, 72, 153, 0.12)',
            borderColor: 'rgba(236, 72, 153, 0.4)',
            color: '#f472b6',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
        >
          <Bug size={12} />
          <span>Calc 5 ÷ 0 (2.7k ⭐)</span>
        </button>
      </div>

      {/* Right: AI Voice Briefing & Engine Switcher */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Voice Copilot Briefing Button */}
        <button
          className="btn btn-sm"
          onClick={speakVoiceBriefing}
          style={{
            background: isSpeaking ? 'var(--accent)' : 'rgba(16, 185, 129, 0.12)',
            borderColor: 'var(--accent)',
            color: isSpeaking ? '#000' : 'var(--accent)',
            fontSize: 11,
            fontWeight: 600,
            padding: '4px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
          title="Listen to an autonomous audio briefing explaining the live incident"
        >
          <Volume2 size={12} className={isSpeaking ? 'animate-pulse' : ''} />
          <span>{isSpeaking ? 'Narrating...' : 'AI Voice Briefing'}</span>
        </button>

        {/* Engine switcher tabs */}
        <div className="tab-bar" style={{ padding: '2px 4px' }}>
          <button
            className={`tab ${agentMode === 'deterministic' ? 'active' : ''}`}
            onClick={() => setAgentMode('deterministic')}
            title="Fast deterministic WebMCP pipeline"
            style={{ fontSize: 10, padding: '3px 8px' }}
          >
            Deterministic ($0)
          </button>
          <button
            className={`tab ${agentMode === 'llm' ? 'active' : ''}`}
            onClick={() => setAgentMode('llm')}
            title="Google Gemini 3.8 Flash live reasoning"
            style={{ fontSize: 10, padding: '3px 8px' }}
          >
            Gemini 3.8 Flash
          </button>
        </div>

        {agentMode === 'llm' && (
          <input
            type="password"
            placeholder="Gemini Key"
            value={geminiApiKey}
            onChange={(e) => setGeminiApiKey(e.target.value)}
            style={{
              width: 90,
              fontSize: 10,
              padding: '2px 6px',
              background: 'var(--bg-input)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              color: 'var(--text-1)',
            }}
            title="Optional: Enter Gemini API key (or leave blank to use server-side key)"
          />
        )}
      </div>
    </div>
  );
};
